import React, { useState, useEffect } from "react";
import { adminApi } from "../../utils/axiosConfig";
import ThreeModelViewer from "../ThreeModelViewer";

import ReactMarkdown from "react-markdown";

export default function ModifyProduct({ product, onProductUpdated }) {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: product.name || "",
    description: product.description || "",
    price: product.price?.originalPrice?.toString() || "",
    promo: product.promo?.toString() || "0",
    type: product.type || "femme",
    variantes: product.variantes?.length
      ? product.variantes.map((v) => ({
          couleur: v.couleur || "",
          stock: v.stock || [{ taille: "", quantity: 0 }],
          images: v.images || [],
        }))
      : [{ couleur: "", stock: [{ taille: "", quantity: 0 }], images: [] }],
  });
  const [newImages, setNewImages] = useState(
    product.variantes?.map(() => []) || [[]]
  );
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pour la responsivité simple (si tu veux, on peut aussi gérer plus finement)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  const promoPrice =
    Number(formData.promo) > 0
      ? (
          Number(formData.price || 0) *
          (1 - Number(formData.promo) / 100)
        ).toFixed(2)
      : null;

  const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${apiUrl}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVariantChange = (index, field, value) => {
    const updated = [...formData.variantes];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, variantes: updated }));
  };

  const handleAddVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variantes: [
        ...prev.variantes,
        { couleur: "", stock: [{ taille: "", quantity: 0 }], images: [] },
      ],
    }));
    setNewImages((prev) => [...prev, []]);
  };

  const handleRemoveVariant = (index) => {
    if (formData.variantes.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      variantes: prev.variantes.filter((_, i) => i !== index),
    }));
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStockChange = (variantIndex, stockIndex, field, value) => {
    const updated = [...formData.variantes];
    updated[variantIndex].stock[stockIndex][field] =
      field === "quantity" ? Number(value) : value;
    setFormData((prev) => ({ ...prev, variantes: updated }));
  };

  const handleAddStock = (variantIndex) => {
    const updated = [...formData.variantes];
    updated[variantIndex].stock.push({ taille: "", quantity: 0 });
    setFormData((prev) => ({ ...prev, variantes: updated }));
  };

  const handleRemoveStock = (variantIndex, stockIndex) => {
    const updated = [...formData.variantes];
    if (updated[variantIndex].stock.length <= 1) return;
    updated[variantIndex].stock = updated[variantIndex].stock.filter(
      (_, i) => i !== stockIndex
    );
    setFormData((prev) => ({ ...prev, variantes: updated }));
  };

  const removeExistingImage = (variantIndex, imgIndex) => {
    const updated = [...formData.variantes];
    updated[variantIndex].images.splice(imgIndex, 1);
    setFormData((prev) => ({ ...prev, variantes: updated }));
  };

  const handleFileChange = (variantIndex, e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => {
      const updated = [...prev];
      updated[variantIndex] = [...(updated[variantIndex] || []), ...files];
      return updated;
    });
  };

  const removeNewImage = (variantIndex, fileIndex) => {
    setNewImages((prev) => {
      const updated = [...prev];
      updated[variantIndex].splice(fileIndex, 1);
      return updated;
    });
  };

  const sanitizeColor = (color) => {
    return color
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("name", formData.name.trim());
      form.append("description", formData.description);
      form.append("price", parseFloat(formData.price));
      form.append("promo", parseFloat(formData.promo));
      form.append("type", formData.type);

      const variantesForBackend = formData.variantes.map((v) => ({
        couleur: v.couleur,
        stock: v.stock,
        images: (v.images || []).filter(
          (img) => typeof img === "string" && img.trim() !== ""
        ),
      }));
      form.append("variantes", JSON.stringify(variantesForBackend));

      newImages.forEach((files, variantIndex) => {
        const colorField = sanitizeColor(
          formData.variantes[variantIndex].couleur
        );
        if (!colorField) return;
        files.forEach((file) => {
          form.append(`images-${colorField}`, file);
        });
      });

      await adminApi.put(`/api/admin/products/${product._id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage("Produit mis à jour !");
      setIsEditing(false);
      if (onProductUpdated) onProductUpdated();
    } catch (err) {
      console.error(err);
      setMessage("Erreur lors de la mise à jour.");
    } finally {
      setLoading(false);
    }
  };

  const buttonStyle = (bg = "#007bff") => ({
    padding: "6px 12px",
    backgroundColor: bg,
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: "bold",
    margin: "4px 4px 4px 0",
    fontSize: isMobile ? 14 : 12,
  });

  if (!isEditing) {
    return (
      <button
        onClick={() => setIsEditing(true)}
        style={{
          padding: "6px 12px",
          backgroundColor: "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
          fontWeight: "bold",
          fontSize: isMobile ? "1rem" : "0.8rem",
          lineHeight: 1,
        }}
      >
        ✏️ Modifier
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: 10,
        border: "1px solid #ccc",
        maxWidth: isMobile ? "100%" : 800,
        margin: "0 auto",
        fontFamily: "Georgia, serif",
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <label>Nom:</label>
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={{ width: "100%", padding: 8, fontSize: isMobile ? 16 : 14 }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>Description (Markdown autorisé):</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={6}
          style={{
            width: "100%",
            padding: 8,
            fontSize: isMobile ? 16 : 14,
            resize: "vertical",
          }}
        />
        <div style={{ marginTop: 8 }}>
          <strong>Aperçu:</strong>
          <div
            style={{
              border: "1px solid #ccc",
              padding: 8,
              backgroundColor: "#f9f9f9",
              fontSize: isMobile ? 16 : 14,
              marginTop: 4,
              borderRadius: 4,
            }}
          >
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginBottom: "1rem",
                    }}
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      marginBottom: "0.75rem",
                    }}
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    style={{ marginBottom: "0.8rem", color: "#3e2c1c" }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    style={{
                      paddingLeft: "1.5rem",
                      marginBottom: "1rem",
                      listStyleType: "disc",
                      color: "#3e2c1c",
                    }}
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    style={{
                      paddingLeft: "1.5rem",
                      marginBottom: "1rem",
                      listStyleType: "decimal",
                      color: "#3e2c1c",
                    }}
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li style={{ marginBottom: "0.4rem" }} {...props} />
                ),
              }}
            >
              {formData.description}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>Prix original (MAD):</label>
        <input
          name="price"
          type="number"
          min="0"
          step="0.01"
          value={formData.price}
          onChange={handleChange}
          style={{ width: "100%", padding: 8 }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>Promotion (%):</label>
        <input
          name="promo"
          type="number"
          min="0"
          max="100"
          step="1"
          value={formData.promo}
          onChange={handleChange}
          style={{ width: "100%", padding: 8 }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>Prix promo calculé :</label>
        <input
          value={promoPrice ?? "Aucune promo"}
          readOnly
          style={{
            width: "100%",
            padding: 8,
            backgroundColor: "#eee",
            color: "#333",
            fontWeight: "bold",
          }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>Type:</label>
        <select
          name="type"
          value={formData.type}
          onChange={handleChange}
          style={{ width: "100%", padding: 8, fontSize: isMobile ? 16 : 14 }}
        >
          <option value="femme">Femme</option>
          <option value="homme">Homme</option>
          <option value="enfant">Enfant</option>
          <option value="collections speciales">Collections spéciales</option>
        </select>
      </div>

      {formData.variantes.map((v, vIndex) => (
        <div
          key={vIndex}
          style={{
            borderTop: "1px solid #eee",
            marginTop: 10,
            paddingTop: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <label style={{ flex: isMobile ? "1 1 100%" : "0 0 100px" }}>
              Couleur:
            </label>
            <input
              value={v.couleur}
              onChange={(e) =>
                handleVariantChange(vIndex, "couleur", e.target.value)
              }
              required
              style={{
                flex: "1 1 auto",
                padding: 8,
                fontSize: isMobile ? 16 : 14,
              }}
            />
            <button
              onClick={() => handleRemoveVariant(vIndex)}
              type="button"
              style={buttonStyle("#dc3545")}
            >
              ❌ Supprimer variante
            </button>
          </div>

          <div>
            <h5>Stocks</h5>
            {v.stock.map((s, sIndex) => (
              <div
                key={sIndex}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <label style={{ flex: isMobile ? "1 1 100%" : "0 0 70px" }}>
                  Taille:
                </label>
                <input
                  value={s.taille}
                  onChange={(e) =>
                    handleStockChange(vIndex, sIndex, "taille", e.target.value)
                  }
                  style={{
                    flex: "1 1 auto",
                    padding: 6,
                    fontSize: isMobile ? 16 : 14,
                  }}
                />

                <label style={{ flex: isMobile ? "1 1 100%" : "0 0 90px" }}>
                  Quantité:
                </label>
                <input
                  type="number"
                  value={s.quantity}
                  onChange={(e) =>
                    handleStockChange(
                      vIndex,
                      sIndex,
                      "quantity",
                      e.target.value
                    )
                  }
                  style={{
                    flex: "1 1 auto",
                    padding: 6,
                    fontSize: isMobile ? 16 : 14,
                  }}
                />

                <button
                  type="button"
                  onClick={() => handleRemoveStock(vIndex, sIndex)}
                  style={buttonStyle("#ffc107")}
                >
                  ❌ Supprimer taille
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddStock(vIndex)}
              style={buttonStyle("#17a2b8")}
            >
              ➕ Ajouter taille
            </button>
          </div>

          <div style={{ marginTop: 10 }}>
            <h5>Images existantes</h5>
            {v.images && v.images.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {v.images.map((filename, i) => {
                  const url = buildImageUrl(filename);
                  return (
                    <div
                      key={i}
                      style={{
                        position: "relative",
                        width: 100,
                        height: 100,
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        overflow: "hidden",
                      }}
                    >
                      {filename.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                        <img
                          src={url}
                          alt="Produit"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <ThreeModelViewer url={url} width={100} height={100} />
                      )}
                      <button
                        type="button"
                        onClick={() => removeExistingImage(vIndex, i)}
                        style={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          background: "red",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: 24,
                          height: 24,
                          cursor: "pointer",
                          fontWeight: "bold",
                          lineHeight: 1,
                        }}
                        title="Supprimer cette image"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p>Aucune image</p>
            )}
          </div>

          <div style={{ marginTop: 10 }}>
            <h5>Ajouter des images</h5>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.glb,.gltf,.obj"
              multiple
              onChange={(e) => handleFileChange(vIndex, e)}
              style={{ width: "100%" }}
            />
            {newImages[vIndex] && newImages[vIndex].length > 0 && (
              <div
                style={{
                  marginTop: 5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                {newImages[vIndex].map((file, i) => (
                  <div
                    key={i}
                    style={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      overflow: "hidden",
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      textAlign: "center",
                      padding: 5,
                    }}
                  >
                    {file.name.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div>{file.name}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeNewImage(vIndex, i)}
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        background: "red",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        cursor: "pointer",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                      title="Supprimer cette image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={handleAddVariant}
          style={buttonStyle("#20c997")}
        >
          ➕ Ajouter une variante
        </button>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 10,
          flexWrap: isMobile ? "wrap" : "nowrap",
          justifyContent: "flex-start",
        }}
      >
        <button
          type="submit"
          disabled={loading}
          style={buttonStyle(loading ? "#aaa" : "#28a745")}
        >
          {loading ? "En cours..." : "💾 Enregistrer"}
        </button>
        <button
          type="button"
          onClick={() => setIsEditing(false)}
          style={buttonStyle("#6c757d")}
        >
          Annuler
        </button>
      </div>

      {message && (
        <div
          style={{
            marginTop: 10,
            color: message.includes("mis à jour") ? "green" : "red",
            fontWeight: "bold",
          }}
        >
          {message}
        </div>
      )}
    </form>
  );
}
